import React, { Fragment, useEffect, useRef, useState } from 'react';
import ToggleSwitch from '../toggle-switch';
import ZipForm from '../zip-form';
import TerritoryManagerCard from './card';
import Loader from '../listing-states/loader';
import { GeolocationType } from '../../helpers/global-storage';
import {
  getUrlParamValue,
  setUrlParamValue,
  setUrlParams,
  updateEndpointWithQueryString
} from '../../helpers/queryString';
import {
  getLocationByIpAddress,
  getZipAndCountryFromUrl,
  getGeolocationDataFromBrowserAndGoogle,
  updateGeolocationDataProperties,
  getStoredGeolocationCountryAndZip
} from '../../helpers/geolocation';
import { useFetch } from '../../hooks/useFetch';
import { TerritoryManagersResponse } from './response';
import { ExtendedWindowType } from '../../helpers/global-types';

type SwitchProps = {
  field: string;
  values: Array<{ value: string; label: string; default: boolean }>;
};

type CommercialArrayType = {
  heading: string;
  copy: string;
  titles: string;
  noResultsMessage: string;
}[];

type TerritoryManagerLookupProps = {
  heading: string;
  introText: string;
  country: string;
  zipPlaceholders: string | { [key: string]: string };
  zipRegex: string | { [key: string]: string };
  switchData: string | SwitchProps;
  zipInvalidMessage: string;
  redirectUrl: string;
  tenant: string;
  sharedKey: string;
  endpoint: string;
  token: string;
  defaultImages: string | { [key: string]: string };
  loadingText: string;
  commercialArray: string;
  residentialArray: string;
};

const RESIDENTIAL_VALUE = 'R';
const COMMERCIAL_VALUE = 'C';

function TerritoryManagerListing({
  heading,
  switchData,
  country,
  zipInvalidMessage,
  zipPlaceholders,
  zipRegex,
  tenant,
  endpoint,
  defaultImages,
  loadingText,
  commercialArray,
  residentialArray
}: TerritoryManagerLookupProps) {
  //Define constants from props
  const switchDataObj: SwitchProps =
    typeof switchData === 'string' ? JSON.parse(switchData) : switchData;

  const [switchDefaultValue, setSwitchDefaultValue] = useState(
    switchDataObj.values.find(val => val.default)?.value || ''
  );
  const defaultImagesObj =
    typeof defaultImages === 'string'
      ? JSON.parse(defaultImages)
      : defaultImages;
  const typeFromUrl = getUrlParamValue('BusinessAreas');

  const regex = typeof zipRegex === 'string' ? JSON.parse(zipRegex) : zipRegex;
  const placeholders =
    typeof zipPlaceholders === 'string'
      ? JSON.parse(zipPlaceholders)
      : zipPlaceholders;

  const commercialArrayObj = JSON.parse(commercialArray) as CommercialArrayType;
  const residentialArrayObj = JSON.parse(
    residentialArray
  ) as CommercialArrayType;

  //Define states
  const defaultCardsRef = useRef<
    TerritoryManagersResponse['territoryManagers']
  >([]);
  const [type, setType] = useState(typeFromUrl || switchDefaultValue);
  const [locationData, setLocationData] = useState<GeolocationType | null>(
    null
  );
  const [endpointUrl, setEndpointUrl] = useState('');
  const [cards, setCards] = useState<
    TerritoryManagersResponse['territoryManagers']
  >([]);
  const [showLoader, setShowLoader] = useState(false);

  //get data from endpoint
  const { data, error } = useFetch<TerritoryManagersResponse>(endpointUrl);

  //Define functions

  const parseCountry = (country: string) => {
    if (country.toUpperCase() === 'US') {
      return 'USA';
    }

    if (country.toUpperCase() === 'CA') {
      return 'CAN';
    }

    return country;
  };

  const countryToCode = (country: string) => {
    if (country.toUpperCase() === 'USA') {
      return 'US';
    }

    if (country.toUpperCase() === 'CAN') {
      return 'CA';
    }

    return country;
  };

  const getTerritoryManagerLocationData = async () => {
    const dataFromUrl = getZipAndCountryFromUrl();

    if (dataFromUrl) {
      return dataFromUrl;
    }

    const storedData = getStoredGeolocationCountryAndZip();

    if (storedData) {
      return storedData;
    }

    const maxmindData = await getLocationByIpAddress();

    if (maxmindData) {
      return maxmindData;
    }

    const browserData = await getGeolocationDataFromBrowserAndGoogle();

    if (browserData) {
      return browserData;
    }

    return null;
  };

  const initListing = async () => {
    const location: GeolocationType | null =
      await getTerritoryManagerLocationData();

    if (location) {
      const newCountry = parseCountry(location.country);
      setLocationData({
        zip: location.zip,
        country: newCountry
      });
      (window as ExtendedWindowType).dataLayer.push({
        event: 'zip_code',
        event_action: 'zip code automatically detected',
        event_category: 'geo info',
        event_attributes: {
          zip_code: location.zip,
          zip_code_country: location.country.toLowerCase()
        }
      });
    } else {
      const newCountry = parseCountry(country);

      setLocationData({
        zip: '',
        country: newCountry
      });
    }
  };

  const updateEndpoint = () => {
    let queryUrl = updateEndpointWithQueryString(endpoint);
    queryUrl = `${queryUrl}&Tenant=${tenant}`;
    setEndpointUrl(queryUrl);
  };

  const updateToCommercial = (e: CustomEvent) => {
    if (e.detail) {
      setSwitchDefaultValue(switchDataObj.values[1].value);
      setType(switchDataObj.values[1].value);
    } else {
      setSwitchDefaultValue(switchDataObj.values[0].value);
      setType(switchDataObj.values[0].value);
    }
  };

  //Define effects

  useEffect(() => {
    window.addEventListener('territoryChange', e =>
      updateToCommercial(e as CustomEvent)
    );
    initListing();

    return () => {
      window.removeEventListener('territoryChange', e =>
        updateToCommercial(e as CustomEvent)
      );
    };
  }, []);

  useEffect(() => {
    setCards([]);

    if (data) {
      data.territoryManagers.sort((a, b) => {
        const nameA = a.name.toUpperCase();
        const nameB = b.name.toUpperCase();

        if (nameA < nameB) {
          return -1;
        }

        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });

      setCards(data.territoryManagers);
    }

    if (!data && !error && endpointUrl) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [data, error]);

  useEffect(() => {
    if (locationData) {
      setUrlParams({
        PostalCode: locationData.zip.replace(/\s/g, ''),
        CountryCode: locationData.country,
        BusinessAreas: type
      });
    }

    if (locationData?.zip && locationData.country && type) {
      updateEndpoint();
    }
  }, [locationData]);

  //Define listeners
  const handleSwitchValueChange = (value: string) => {
    setCards([]);
    setType(value);

    if (locationData?.zip && locationData.country && value) {
      setUrlParams({
        PostalCode: locationData.zip.replace(/\s/g, ''),
        CountryCode: locationData.country,
        BusinessAreas: value
      });

      updateEndpoint();
    } else {
      setUrlParamValue('BusinessAreas', value);
    }
  };

  const handleClear = () => {
    setLocationData({
      zip: '',
      country: parseCountry(country)
    });
  };

  const handleSubmit = (zip: string, country: string) => {
    if (zip && country) {
      setUrlParams({
        PostalCode: zip.replace(/\s/g, ''),
        CountryCode: parseCountry(country)
      });

      updateGeolocationDataProperties({
        zip,
        country: countryToCode(country).toLowerCase()
      });
    }

    setLocationData({
      zip,
      country: parseCountry(country)
    });

    updateEndpoint();
  };

  const renderCardsSections = (arrayObj: CommercialArrayType) => {
    defaultCardsRef.current = cards;

    let filteredCards = cards,
      isDefault = false;
    const sectionsLength = arrayObj.length;

    return arrayObj.map((item, index) => {
      if (index === sectionsLength - 1 && !item.titles) {
        isDefault = true;
        filteredCards = defaultCardsRef.current;
      } else {
        const titles = item.titles.split('|');
        filteredCards = cards.filter(card => {
          if (titles.includes(card.title)) {
            defaultCardsRef.current = defaultCardsRef.current.filter(
              defaultCard => defaultCard !== card
            );

            return true;
          }

          return false;
        });
      }

      if (filteredCards.length < 1 && isDefault && sectionsLength > 1) {
        return null;
      }

      return (
        <Fragment key={`${item.heading}-${index}`}>
          {item.heading && (
            <h3 className="territory-manager-listing__subheading">
              {item.heading}
            </h3>
          )}
          {item.copy && (
            <div
              className="territory-manager-listing__description rtf"
              dangerouslySetInnerHTML={{
                __html: item.copy
              }}
            />
          )}
          {filteredCards.length > 0 ? (
            <ul className="territory-manager-listing__list">
              {filteredCards.map((card, index) => (
                <li key={index} className="territory-manager-list-item">
                  <TerritoryManagerCard
                    name={card.name}
                    title={card.title}
                    email={card.email}
                    phone={card.mobilePhoneNumber || card.businessPhoneNumber}
                    image={card.profilePhotoUrl || defaultImagesObj[type]}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <>
              {item.noResultsMessage && (
                <div className="row territory-manager-listing__validation-error-wrapper">
                  <div className="zip-form__error-message territory-manager-listing__error-message">
                    <p className="error-message__error ">
                      {item.noResultsMessage}
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </Fragment>
      );
    });
  };

  return (
    <div className="territory-manager-listing container">
      <h2 className="territory-manager-listing__heading">{heading}</h2>
      <div className="territory-manager-listing__wrapper row">
        <div className="territory-manager-listing__switch">
          <ToggleSwitch
            values={switchDataObj.values}
            onValueChange={handleSwitchValueChange}
            defaultValue={type || switchDefaultValue}
          />
        </div>
        <div className="territory-manager-listing__form">
          {locationData && (
            <ZipForm
              defaultCountryValue={
                locationData?.country.toLowerCase() || country || 'us'
              }
              invalidZipMessage={zipInvalidMessage}
              onSubmit={handleSubmit}
              zipPlaceholders={placeholders}
              zipRegex={regex}
              whiteBox={true}
              onClear={handleClear}
              defaultZipValue={locationData.zip}
              alternativeOptions={true}
            />
          )}
        </div>
      </div>
      {showLoader && <Loader loaderText={loadingText} />}
      {type === RESIDENTIAL_VALUE && (
        <div className="territory-manager-listing__wrapper--commercial">
          {renderCardsSections(residentialArrayObj)}
        </div>
      )}
      {type === COMMERCIAL_VALUE && (
        <div className="territory-manager-listing__wrapper--commercial">
          {renderCardsSections(commercialArrayObj)}
        </div>
      )}
    </div>
  );
}

export default TerritoryManagerListing;
